import {useCallback, useEffect, useRef, useState} from "react";
import ServiceClient from "../../utils/ServiceClient";
import {
  buildSafeFinixTokenizationErrorLog, finixTokenizationPreparationErrorMessage, isFinixConfigured,
  mountFinixCardForm, submitFinixCardForm, type FinixForm
} from "../../services/finixTokenization";
import {FinixInstrumentType} from "../../types";
import {isGatewayTimeout, isRequestTimeout} from "./utils";

/**
 * Drives Finix hosted-field card entry for the withdraw flow (SMG-4557).
 *
 * <p>Deliberately NOT shaped like {@code useCardTokenization} next door, because the providers are
 * not shaped alike. eProtect takes a PAN this app already holds and exchanges it for a token, so
 * its hook can expose {@code tokenizeCard(number, cvc)} and let our own inputs own the data. Finix
 * exposes only form constructors and runs the tokenization request from inside its own
 * cross-origin iframe — so Finix must own the inputs, and this hook mounts a form rather than
 * tokenizing a value. That is the point of it: the PAN never enters our origin.</p>
 *
 * <p>Consequence worth stating plainly: a card entered here is tokenized with Finix and NOT with
 * WorldPay. Only one provider can own the fields, so it is a Finix destination and WorldPay is not
 * a fallback for it — there is no cnp token to fall back with. The caller must register the
 * returned {@code finix:<id>} handle as a selectable destination; paygate accepts that namespace as
 * a payout {@code cardUid} and re-validates ownership server-side.</p>
 */

const mountElementId = 'finix-card-form';

const finixSaveErrorMessage = 'We could not save this card. Please check the details and try again.';

/**
 * A save that ran out of time (SMG-4797): paygate's 503 when Finix did not answer, a gateway's 504/502
 * when the ingress gave up on paygate, or this app giving up on paygate first. Finix being slow, not the card, so the message says try again. Unlike the cnp form,
 * no "check your saved cards" step is needed before a retry: a user has one Finix payout card, a retry
 * replaces it, and an instrument a request we abandoned may still have created is never paid (payouts
 * pay only the instrument on the user's row when the withdrawal is accepted).
 */
const finixSaveTimeoutCode = 'FINIX_CARD_SAVE_TIMEOUT';
const finixSaveTimeoutMessage = 'Our payout provider did not respond in time. Please try again.';

const saveErrorCode = (error: unknown) => {
  const code = (error as {response?: {data?: {code?: unknown}}})?.response?.data?.code;
  return typeof code === 'string' ? code : undefined;
};

const finixIneligibleCardMessage =
  'This card cannot receive instant payouts. Try a different card.';

type Status = 'idle' | 'loading' | 'ready' | 'error';

export const useFinixCardEntry = (isActive: boolean) => {
  const isSupported = isFinixConfigured();
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const formRef = useRef<FinixForm>();
  const unmountRef = useRef<() => void>();
  // Bumped on every (re)mount so a form that was torn down mid-flight cannot resolve into the
  // state of a later one — the same staleness guard useSavedCardManagement applies to saves.
  const mountIdRef = useRef(0);

  const teardown = useCallback(() => {
    mountIdRef.current += 1;
    formRef.current = undefined;
    try {
      unmountRef.current?.();
    } catch {
      // The host element is already gone (unmount race); nothing to detach.
    }
    unmountRef.current = undefined;
  }, []);

  const mount = useCallback(() => {
    if (!isSupported) return;

    const mountId = ++mountIdRef.current;
    setStatus('loading');
    setErrorMessage('');

    // The element must exist before Finix resolves it by id, and it is rendered by the step this
    // hook serves — so mounting waits a frame rather than racing React's commit.
    const attempt = () => mountFinixCardForm(mountElementId).then(({form, unmount}) => {
      if (mountId !== mountIdRef.current) {
        unmount();
        return;
      }
      formRef.current = form;
      unmountRef.current = unmount;
      setStatus('ready');
    }).catch(error => {
      if (mountId !== mountIdRef.current) return;
      console.log('finix mount error', buildSafeFinixTokenizationErrorLog(error));
      setStatus('error');
      setErrorMessage(finixTokenizationPreparationErrorMessage);
    });

    const frame = setTimeout(attempt, 0);
    return () => clearTimeout(frame);
  }, [isSupported]);

  useEffect(() => {
    if (!isActive || !isSupported) {
      teardown();
      setStatus('idle');
      // Per-attempt state dies with the attempt: re-entering the step must not greet the user with
      // a stale "could not save this card" from the previous one.
      setIsSaving(false);
      setFormError('');
      return;
    }
    const cancelPendingMount = mount();
    return () => {
      cancelPendingMount?.();
      teardown();
    };
  }, [isActive, isSupported, mount, teardown]);

  const retry = useCallback(() => {
    teardown();
    mount();
  }, [mount, teardown]);

  /**
   * Tokenize with Finix and register the result as a payout destination. Resolves the provisioned
   * instrument, or null when the attempt failed (the message is on {@code formError}).
   */
  const saveCard = useCallback(async (phone: string): Promise<FinixInstrumentType | null> => {
    const form = formRef.current;
    if (isSaving || status !== 'ready' || !form) {
      setFormError(errorMessage || finixTokenizationPreparationErrorMessage);
      return null;
    }

    const mountId = mountIdRef.current;
    setIsSaving(true);
    setFormError('');

    try {
      const {token} = await submitFinixCardForm(form);
      // The form can be torn down while Finix's iframe is still working; a token that arrives
      // after that belongs to a screen the user has left, so it must not update this one.
      if (mountId !== mountIdRef.current) return null;

      const payGateToken = await ServiceClient.getPayGateToken();
      if (mountId !== mountIdRef.current) return null;

      const instrument = await ServiceClient.saveFinixInstrument(token, phone, payGateToken);
      if (mountId !== mountIdRef.current) return null;

      // INELIGIBLE means Finix said this card cannot receive a push-to-card payout. The endpoint
      // returns it precisely so the client can say so now; reporting a successful save and letting
      // the user discover it at payout time is the failure this field exists to prevent.
      if (!instrument.provisioned || instrument.eligibility === 'INELIGIBLE') {
        setFormError(finixIneligibleCardMessage);
        return null;
      }

      return instrument;
    } catch (error) {
      if (mountId !== mountIdRef.current) return null;
      console.log('finix saveCard error', buildSafeFinixTokenizationErrorLog(error));
      const timedOut = isRequestTimeout(error) || isGatewayTimeout(error) || saveErrorCode(error) === finixSaveTimeoutCode;
      setFormError(timedOut ? finixSaveTimeoutMessage : finixSaveErrorMessage);
      return null;
    } finally {
      // Unconditionally, never behind the mount-id guard. That guard decides which writes to DROP
      // because they belong to a form the user has left; releasing our own in-flight flag is the
      // teardown half of the lifecycle, not a stale write. Gating it meant cancelling mid-save
      // left isSaving stuck true forever — and WithdrawFlowModal is mounted unconditionally, so
      // the stuck state survived closing and reopening the whole flow.
      setIsSaving(false);
    }
  }, [errorMessage, isSaving, status]);

  return {isSupported, mountElementId, status, errorMessage, formError, isSaving, retry, saveCard};
};
